import { useRef, useState } from "react";
import { Pencil } from "lucide-react";
import {
  isBalanceAdjustmentReasonValid,
  normalizeBalanceAdjustmentAmount,
  round2,
} from "@payday/contracts";
import { api } from "../../lib/api.js";
import { Button, Modal } from "../ui/index.js";
import { crossCheckClass } from "./MetricCells.js";

export const OvertimePotEditButton = ({
  value,
  balanceHours,
  monthlyDeltaValue,
  employeeName,
  disabled,
  onClick,
}: {
  value: string;
  balanceHours: number;
  monthlyDeltaValue?: string;
  employeeName: string;
  disabled: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={`${crossCheckClass(balanceHours > 0 ? { state: "green" } : balanceHours < 0 ? { state: "red" } : undefined)} min-h-7 cursor-pointer items-center gap-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
    style={{
      color:
        balanceHours > 0
          ? "var(--payday-success)"
          : balanceHours < 0
            ? "var(--payday-danger)"
            : "var(--payday-foreground)",
    }}
    disabled={disabled}
    onClick={onClick}
    aria-label={`Edit overtime pot for ${employeeName}. Saved balance ${value}`}
    title={`Adjust the saved overtime balance in hours${monthlyDeltaValue ? `. Monthly change ${monthlyDeltaValue}` : ""}`}
  >
    <span aria-label={`Overtime balance for ${employeeName}: ${value}`}>
      {value}
    </span>
    <Pencil size={13} aria-hidden="true" />
  </button>
);

export const OvertimePotDialog = ({
  employeeId,
  employeeName,
  currentHours,
  month,
  onClose,
  onSaved,
}: {
  employeeId: number;
  employeeName: string;
  currentHours: number;
  month: string;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) => {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [potHours, setPotHours] = useState(currentHours);
  const submitted = useRef<{
    key: string;
    payload: Record<string, string | number>;
  } | null>(null);
  const normalized = normalizeBalanceAdjustmentAmount(amount);
  const valid = normalized !== null;
  const delta = normalized ?? 0;
  const after = round2(potHours + delta);
  const save = async () => {
    if (saving || !valid || !isBalanceAdjustmentReasonValid(reason)) return;
    setSaving(true);
    setError("");
    let saved = false;
    try {
      const input = {
        userId: employeeId,
        balanceType: "overtime_hours" as const,
        inputAmount: delta,
        inputUnit: "hours" as const,
        effectiveMonth: month,
      };
      const key = JSON.stringify([input, reason.trim()]);
      if (submitted.current?.key !== key) {
        const preview = await api.previewOpenProjectBalanceAdjustment(input);
        if (preview.beforeEffectiveBalanceHours === null)
          throw new Error(
            "Recalculate balances before adjusting the overtime pot.",
          );
        if (preview.beforeEffectiveBalanceHours !== potHours) {
          setPotHours(preview.beforeEffectiveBalanceHours);
          setError(
            "The overtime pot changed. Review the updated value, then save again.",
          );
          return;
        }
        submitted.current = {
          key,
          payload: {
            ...input,
            requestId: crypto.randomUUID(),
            adjustmentType: "manual",
            expectedRevision: preview.revision,
            conversionBasis: preview.conversionBasis,
            reason: reason.trim(),
          },
        };
      }
      await api.createOpenProjectBalanceAdjustment(submitted.current.payload);
      saved = true;
      await onSaved();
      onClose();
    } catch (failure) {
      if (
        failure instanceof Error &&
        "code" in failure &&
        [
          "balance_adjustment_revision_stale",
          "balance_adjustment_conversion_basis_stale",
        ].includes(String(failure.code))
      )
        submitted.current = null;
      if (saved) onClose();
      else
        setError(
          failure instanceof Error
            ? failure.message
            : "Could not save the adjustment. Try again.",
        );
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      title="Edit overtime pot"
      description={`${employeeName} · ${month}. Current pot: ${potHours.toFixed(2)} h. This records an audited adjustment and requires balance recalculation.`}
      size="sm"
      isDismissable={!saving}
      onOpenChange={(open: boolean) => {
        if (!open && !saving) onClose();
      }}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={
              saving || !valid || !isBalanceAdjustmentReasonValid(reason)
            }
            loading={saving}
            onClick={() => void save()}
          >
            Save adjustment
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <label className="grid gap-2 text-sm">
          Change in hours
          <input
            autoFocus
            inputMode="decimal"
            placeholder="+1 or -1"
            value={amount}
            disabled={saving}
            onChange={(event) => setAmount(event.target.value)}
            className="rounded-lg border border-border bg-background p-3"
          />
        </label>
        <p role="status" className="text-sm text-foreground-muted">
          {valid
            ? `New overtime pot: ${after.toFixed(2)} h`
            : "Enter a nonzero change in hours, for example +1.5 or -1. Changes use two decimal places."}
        </p>
        <label className="grid gap-2 text-sm">
          Reason (at least 8 characters)
          <textarea
            value={reason}
            disabled={saving}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
            className="rounded-lg border border-border bg-background p-3"
          />
        </label>
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};
